import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Download, FileText, BookOpen, MoreVertical, Trash2, UserX, UserCheck, ArrowLeft, Layers, CheckCircle, BarChart2, LayoutGrid, List as ListIcon, Book, Clock } from 'lucide-react';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Table from '../../components/Common/Table';
import Badge from '../../components/Common/Badge';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ShardLoader from '../../components/Common/ShardLoader';
import useGlobalSearch from '../../hooks/useGlobalSearch';

const ManageCourses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery: searchTerm, setSearchQuery: setSearchTerm, debouncedSearchQuery: debouncedSearch, searchInputRef } = useGlobalSearch('', 400, () => setCurrentPage(1));
  const [filters, setFilters] = useState({
    status: '',
    createdBy: '',
    department: '',
    category: ''
  });
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('coursesViewMode') || 'list');

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('coursesViewMode', mode);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' },
  ];

  useEffect(() => {
    fetchCourses();
  }, [currentPage, debouncedSearch, filters, sortConfig]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch.trim() || undefined,
        status: filters.status || undefined,
        department: filters.department || undefined,
        category: filters.category || undefined,
        // Optional: sorting could be added here if backend supports it
      };

      const { data } = await api.get('/courses', { params });
      if (data.success) {
        setCourses(data.data);
        setFilteredCourses(data.data);
        setTotalPages(data.totalPages || 1);
        setTotalItems(data.total || data.data.length);
      }
    } catch (error) {
      toast.error('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setCurrentPage(1);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    try {
      const { data } = await api.put(`/courses/${id}`, { status: newStatus });
      if (data.success) {
        toast.success(`Course ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        fetchCourses();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this course? This action cannot be undone.');
    if (!confirmed) return;

    try {
      const { data } = await api.delete(`/courses/${id}`);
      if (data.success) {
        toast.success('Course deleted successfully');
        fetchCourses();
      }
    } catch (error) {
      console.error('Failed to delete course:', error);
      toast.error(error.response?.data?.message || 'Failed to delete course');
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text('Courses List', 14, 22);
    doc.setFontSize(11);
    doc.text(`Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 30);

    const tableData = filteredCourses.map((course) => [
      course.courseCode || 'N/A',
      course.title || 'N/A',
      course.duration ? `${course.duration} min` : '45 min',
      course.createdBy?.fullName || 'N/A',
      course.modules?.length || 0,
      course.status || 'N/A',
    ]);

    doc.autoTable({
      head: [['Code', 'Title', 'Duration', 'Created By', 'Modules', 'Status']],
      body: tableData,
      startY: 35,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [220, 38, 38] },
    });

    doc.save(`courses-list-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF exported successfully');
  };

  const exportToExcel = () => {
    const excelData = filteredCourses.map((course) => ({
      'Course Code': course.courseCode || 'N/A',
      'Title': course.title || 'N/A',
      'Description': course.description || 'N/A',
      'Duration': course.duration ? `${course.duration} min` : '45 min',
      'Created By': course.createdBy?.fullName || 'N/A',
      'Total Modules': course.modules?.length || 0,
      'Status': course.status || 'N/A',
      'Created Date': course.createdAt ? format(new Date(course.createdAt), 'dd/MM/yyyy') : 'N/A',
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Courses');
    XLSX.writeFile(wb, `courses-list-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success('Excel exported successfully');
  };

  // Pagination (Server-Side data is already paginated, so just use it)
  const currentItems = filteredCourses;
  // Calculate index for display
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, totalItems);

  const columns = [
    {
      key: 'courseNumber',
      label: 'Course Number',
      sortable: true,
      render: (course) => (
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 font-mono">
          {course.courseNumber || course.courseCode || 'Pending'}
        </span>
      ),
    },
    {
      key: 'programmeType',
      label: 'Course Program Track',
      sortable: true,
      render: (course) => {
        const typeMap = {
          'HumanIntelligence': 'Human Intelligence',
          'ReadinessTrack': 'Readiness Track',
          'PIQ': 'PIQ - Personal',
          'AIQ': 'AIQ - AI Readiness',
          'SQ': 'SQ - Sustainability'
        };
        const pType = typeMap[course.programmeType] || course.programmeType || 'N/A';
        return (
          <span className="text-sm font-semibold text-gray-700 bg-gray-100 px-2.5 py-1 rounded-lg">
            {pType}
          </span>
        );
      },
    },
    {
      key: 'stage',
      label: 'Intelligence State',
      sortable: true,
      render: (course) => {
        const stageStr = course.stage ? (course.stage.includes('_') ? course.stage.split('_')[1] : course.stage) : 'N/A';
        return (
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {stageStr}
          </span>
        );
      },
    },
    {
      key: 'title',
      label: 'Course Detail',
      sortable: true,
      render: (course) => (
        <div
          className="cursor-pointer hover:bg-gray-50/50 dark:hover:bg-[#001f40]/50 -m-2 p-2 rounded transition-colors"
          onClick={() => navigate(`/courses/${course._id}/builder`)}
          title="Click to manage course content"
        >
          <p className="font-semibold text-gray-900 dark:text-gray-100">{course.title}</p>
          <p className="text-xs text-slate-400 dark:text-slate-300 mt-1">{course.description?.substring(0, 80) || 'No description'}...</p>
        </div>
      ),
    },
    {
      key: 'createdBy',
      label: 'Created By',
      sortable: true,
      render: (course) => (
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          {course.createdBy?.fullName || 'System Admin'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (course) => (
        <span className={`text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-full ${
          course.status === 'active' 
            ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400' 
            : 'bg-rose-50 text-rose-600 dark:bg-rose-950/30 dark:text-rose-400'
        }`}>
          {course.status || 'inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (course) => (
        <div className="relative flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setOpenDropdown(openDropdown === course._id ? null : course._id)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 rounded-lg transition-colors text-gray-500 dark:text-gray-400"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {openDropdown === course._id && (
            <div className="absolute right-0 top-8 z-50 w-44 bg-white dark:bg-[#002147] rounded-xl shadow-xl border border-gray-100 dark:border-[#1a3884]/30 py-1 overflow-hidden">
              <button
                onClick={() => { navigate(`/courses/edit/${course._id}`); setOpenDropdown(null); }}
                className="w-full px-4 py-2 text-left text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 flex items-center transition-colors"
              >
                <Edit className="w-3.5 h-3.5 mr-2.5" />
                Edit Course
              </button>
              <button
                onClick={() => { navigate(`/courses/${course._id}/builder`); setOpenDropdown(null); }}
                className="w-full px-4 py-2 text-left text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 flex items-center transition-colors"
              >
                <BookOpen className="w-3.5 h-3.5 mr-2.5" />
                Course Builder
              </button>
              <button
                onClick={() => { handleToggleStatus(course._id, course.status); setOpenDropdown(null); }}
                className={`w-full px-4 py-2 text-left text-xs flex items-center transition-colors ${
                  course.status === 'active' 
                    ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20' 
                    : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20'
                }`}
              >
                {course.status === 'active' ? (
                  <>
                    <UserX className="w-3.5 h-3.5 mr-2.5" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck className="w-3.5 h-3.5 mr-2.5" />
                    Activate
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];



  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="glass dark:bg-[#002147]/80 rounded-xl shadow-md border border-white/30 dark:border-[#1a3884]/30 p-4 mb-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-blue-400 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884] dark:hover:border-[#1a3884] dark:hover:text-white rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
              title="Back to Dashboard"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#00152E] dark:text-white mb-1 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#1a3884]" />
                Manage Courses
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <span className="font-medium">
                  Create, edit, and manage all learning content
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto justify-start sm:justify-end flex-wrap">
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 text-sm px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#00152e] border border-gray-200 dark:border-[#1a3884]/50 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 rounded-xl transition-colors font-medium shadow-sm"
              title="Export as Excel"
            >
              <Download size={16} className="text-gray-500 dark:text-gray-400" />
              <span className="hidden sm:inline">Export</span>
            </button>

            <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#00152e] p-1 rounded-xl border border-gray-200 dark:border-[#1a3884]/50">
                <button
                  onClick={() => toggleViewMode('grid')}
                  className={`p-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-all duration-300 ${viewMode === 'grid' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                >
                  <LayoutGrid size={16} /> <span className="hidden sm:inline">Grid</span>
                </button>
                <button
                  onClick={() => toggleViewMode('list')}
                  className={`p-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-all duration-300 ${viewMode === 'list' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                >
                  <ListIcon size={16} /> <span className="hidden sm:inline">List</span>
                </button>
            </div>

            <button
              onClick={() => navigate('/courses/create')}
              className="flex items-center gap-2 text-sm px-4 py-2 bg-[#1a3884] hover:bg-[#00152E] dark:bg-[#1a3884] dark:hover:bg-[#00152E] text-white font-semibold rounded-xl transition-all shadow-md shadow-[#1a3884]/15 hover:shadow-lg hover:shadow-[#1a3884]/20 transform hover:-translate-y-0.5 transition-colors border border-[#00152E]/20"
            >
              <Plus size={16} />
              <span className="whitespace-nowrap hidden sm:inline">Create Course</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="group relative bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/35 p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden">
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Courses</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{courses.length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-[#1a3884] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
              <BookOpen className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        <div className="group relative bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/35 p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden">
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Active Courses</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{courses.filter(c => c.status === 'active').length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-[#1a3884] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
              <CheckCircle className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/35 shadow-sm p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-100 mb-4 flex items-center gap-2">Filters & Search</h2>
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 dark:border-[#1a3884]/35 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] transition-all bg-white dark:bg-[#00152E] text-gray-900 dark:text-gray-100 shadow-sm placeholder-gray-400"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="px-3 py-2.5 text-sm border border-gray-200 dark:border-[#1a3884]/35 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] transition-all bg-gray-50 dark:bg-[#00152E] text-gray-700 dark:text-gray-300 min-w-[130px] hover:bg-white dark:hover:bg-[#002147]"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            
            <select
              name="department"
              value={filters.department}
              onChange={handleFilterChange}
              className="px-3 py-2.5 text-sm border border-gray-200 dark:border-[#1a3884]/35 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] transition-all bg-gray-50 dark:bg-[#00152E] text-gray-700 dark:text-gray-300 min-w-[150px] hover:bg-white dark:hover:bg-[#002147]"
            >
              <option value="">All Departments</option>
              <option value="HumanIntelligence">Human Intelligence</option>
              <option value="PIQ">PIQ</option>
              <option value="AIQ">AIQ</option>
              <option value="SQ">SQ</option>
              <option value="ReadinessTrack">Readiness Track</option>
            </select>

            <select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              className="px-3 py-2.5 text-sm border border-gray-200 dark:border-[#1a3884]/35 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] transition-all bg-gray-50 dark:bg-[#00152E] text-gray-700 dark:text-gray-300 min-w-[140px] hover:bg-white dark:hover:bg-[#002147]"
            >
              <option value="">All Categories</option>
              <option value="Capacity">Capacity</option>
              <option value="Capability">Capability</option>
              <option value="Leadership">Leadership</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content View (Grid or List) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 px-2">
           <h2 className="text-base font-bold text-gray-900 dark:text-white">Courses Directory</h2>
           <span className="text-sm text-slate-400 dark:text-slate-300 font-medium">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCourses.length)} of {filteredCourses.length} courses
           </span>
        </div>
        
        {loading ? (
            <div className="py-20 text-center text-slate-400 dark:text-slate-200">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-sm">Loading courses...</p>
            </div>
        ) : filteredCourses.length === 0 ? (
            <div className="py-20 text-center bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-gray-100 dark:border-[#1a3884]/30">
              <BookOpen className="w-14 h-14 text-gray-200 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 font-medium dark:text-gray-300">No courses found</p>
              <p className="text-sm text-slate-400 dark:text-slate-400 mt-1">Try adjusting filters or create a new course.</p>
            </div>
        ) : viewMode === 'list' ? (
          <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/35 shadow-sm overflow-hidden">
            <Table
              columns={columns}
              data={currentItems}
              onSort={handleSort}
              sortConfig={sortConfig}
              wrapperClassName="overflow-visible"
              getRowClassName={(course) => openDropdown === course._id ? 'relative z-50' : ''}
            />
          </div>
        ) : (
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {currentItems.map(course => (
                 <div key={course._id} className="group bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-[#1a3884]/30 p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden flex flex-col">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00152E] to-[#1a3884] dark:from-[#4f7dff] dark:to-[#1a3884] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    
                    <div className="flex justify-between items-start mb-4">
                        <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-[#00152e] border border-indigo-100 dark:border-[#1a3884]/50 flex items-center justify-center text-lg font-bold text-[#1a3884] dark:text-[#4f7dff] shadow-sm shrink-0">
                           <Book className="w-6 h-6" />
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${course.status === 'active' ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30' : 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800/30'}`}>
                          {course.status || 'inactive'}
                        </span>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight mb-2 line-clamp-2" title={course.title}>{course.title}</h3>
                    <div className="mb-3 text-xs text-slate-500 dark:text-slate-400 font-mono">
                       {course.courseNumber || course.courseCode || 'Pending Code'}
                    </div>
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-4">
                       {course.description || 'No description provided.'}
                    </p>

                    <div className="space-y-2 mt-auto pt-4 border-t border-gray-100 dark:border-[#1a3884]/30">
                        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 font-medium">
                            <span>Program Track:</span>
                            <span className="bg-gray-100 dark:bg-[#00152e] px-2 py-0.5 rounded text-gray-700 dark:text-gray-200">
                                {course.programmeType === 'HumanIntelligence' ? 'Human Intelligence' : course.programmeType || 'N/A'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 font-medium">
                            <span>Creator:</span>
                            <span>{course.createdBy?.fullName || 'System'}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-5">
                    <button
                        onClick={() => navigate(`/courses/${course._id}/builder`)}
                        className="w-full py-2 bg-gray-50 dark:bg-[#1a3884]/20 hover:bg-gray-100 dark:hover:bg-[#1a3884]/40 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 transition-colors border border-gray-200 dark:border-[#1a3884]/30"
                    >
                        <BookOpen size={14} /> Builder
                    </button>
                    <button
                        onClick={(e) => {
                           e.stopPropagation();
                           setOpenDropdown(openDropdown === course._id ? null : course._id);
                        }}
                        className="w-full py-2 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 transition-colors border border-blue-100 dark:border-blue-900/30 relative"
                    >
                        <MoreVertical size={14} /> Actions
                        {openDropdown === course._id && (
                             <div className="absolute bottom-full right-0 mb-2 w-44 bg-white dark:bg-[#002147] border border-gray-200 dark:border-[#1a3884]/40 rounded-lg shadow-xl z-[100] py-1 overflow-visible text-left">
                               <button
                                 onClick={() => { navigate(`/courses/edit/${course._id}`); setOpenDropdown(null); }}
                                 className="w-full px-4 py-2.5 text-left text-xs text-gray-700 dark:text-slate-200 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884]/45 flex items-center"
                               >
                                 <Edit className="w-3.5 h-3.5 mr-2.5" /> Edit Course
                               </button>
                               <button
                                  onClick={() => { handleToggleStatus(course._id, course.status); setOpenDropdown(null); }}
                                  className={`w-full px-4 py-2.5 text-left text-xs flex items-center transition-colors ${
                                    course.status === 'active' 
                                      ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20' 
                                      : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20'
                                  }`}
                               >
                                 {course.status === 'active' ? (
                                   <><UserX className="w-3.5 h-3.5 mr-2.5" /> Deactivate</>
                                 ) : (
                                   <><UserCheck className="w-3.5 h-3.5 mr-2.5" /> Activate</>
                                 )}
                               </button>
                               <button
                                onClick={(e) => {
                                   e.stopPropagation();
                                   handleDelete(course._id);
                                   setOpenDropdown(null);
                                }}
                                className="w-full px-4 py-2.5 text-left text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 flex items-center"
                                >
                                <Trash2 className="w-3.5 h-3.5 mr-2.5" /> Delete
                                </button>
                             </div>
                           )}
                    </button>
                    </div>
                 </div>
              ))}
           </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-[#1a3884]/20 bg-gray-50/30 dark:bg-[#001f40]/10">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`inline-flex items-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${currentPage === 1
                  ? 'border-gray-200 dark:border-[#1a3884]/20 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'border-gray-300 dark:border-[#1a3884]/45 text-gray-700 dark:text-gray-300 bg-white dark:bg-[#002147] hover:bg-gray-50 dark:hover:bg-[#002d5c]/60'
                  }`}
              >
                Previous
              </button>

              <div className="flex items-center space-x-1">
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === index + 1
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white dark:bg-[#002147] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#002d5c]/60 border border-gray-300 dark:border-[#1a3884]/45'
                      }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`inline-flex items-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${currentPage === totalPages
                  ? 'border-gray-200 dark:border-[#1a3884]/20 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'border-gray-300 dark:border-[#1a3884]/45 text-gray-700 dark:text-gray-300 bg-white dark:bg-[#002147] hover:bg-gray-50 dark:hover:bg-[#002d5c]/60'
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageCourses;
